import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faFile, faMessage, faUser } from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';

const EMPTY = { people: [], chats: [], messages: [], files: [], more: {} };
const dateLabel = value => value ? new Date(value).toLocaleDateString('en-GB') : '';

/** Shared result display; each search surface supplies its own scope and actions. */
const ChatSearchResults = ({ query, conversationId, onPerson, onChat, onMessage }) => {
    const [page, setPage] = useState(0);
    const [retry, setRetry] = useState(0);
    const [results, setResults] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const term = query.trim();

    useEffect(() => { setPage(0); setRetry(0); setResults(EMPTY); }, [term, conversationId]);
    useEffect(() => {
        if (term.length < 2) { setResults(EMPTY); setLoading(false); setError(''); return undefined; }
        const controller = new AbortController();
        setLoading(true);
        setError('');
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get('/conversations/universal-search', {
                    params: { q: term, limit: 12, page, ...(conversationId ? { conversationId } : {}) },
                    signal: controller.signal
                });
                if (!controller.signal.aborted) setResults(previous => page === 0 ? data : {
                    people: [...previous.people, ...(data.people || [])],
                    chats: [...previous.chats, ...(data.chats || [])],
                    messages: [...previous.messages, ...(data.messages || [])],
                    files: [...previous.files, ...(data.files || [])],
                    more: data.more || {}
                });
            } catch (err) {
                if (!controller.signal.aborted) {
                    if (page === 0) setResults(EMPTY);
                    setError('Search failed. Please try again.');
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }, page === 0 ? 250 : 0);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [term, conversationId, page, retry]);

    if (term.length < 2) return <div className="msgr-empty">Type at least two characters to search.</div>;
    const sections = [
        ...(!conversationId ? [
            { key: 'people', label: 'People', icon: faUser, rows: results.people, render: person => (
                <button key={person._id} className="msgr-search-hit" onClick={() => onPerson?.(person)}>
                    <FontAwesomeIcon icon={faUser} /><span><strong>{person.name}</strong><small>{person.jobTitle || person.employeeId || 'Contact'}</small></span>
                </button>
            ) },
            { key: 'chats', label: 'Chats', icon: faComments, rows: results.chats, render: chat => (
                <button key={chat._id} className="msgr-search-hit" onClick={() => onChat?.(chat)}>
                    <FontAwesomeIcon icon={faComments} /><span><strong>{chat.title}</strong><small>{chat.kind === 'group' ? 'Group chat' : chat.kind === 'app' ? 'App notifications' : 'Direct chat'}</small></span>
                </button>
            ) }
        ] : []),
        { key: 'messages', label: 'Messages', icon: faMessage, rows: results.messages, render: message => (
            <button key={message._id} className="msgr-search-hit is-message" onClick={() => onMessage?.(message)}>
                <FontAwesomeIcon icon={faMessage} /><span><strong>{message.text}</strong>
                    <small>{[message.conversationTitle, message.senderName, dateLabel(message.createdAt)].filter(Boolean).join(' · ')}</small></span>
            </button>
        ) },
        { key: 'files', label: 'Files & media', icon: faFile, rows: results.files, render: file => (
            <button key={`${file.messageId}:${file._id}`} className="msgr-search-hit" onClick={() => onMessage?.({ ...file, _id: file.messageId })}>
                <FontAwesomeIcon icon={faFile} /><span><strong>{file.fileName}</strong>
                    <small>{[file.conversationTitle, file.senderName, dateLabel(file.createdAt)].filter(Boolean).join(' · ')}</small></span>
            </button>
        ) }
    ];
    const any = sections.some(section => section.rows?.length);
    return <div className="msgr-search-results" aria-live="polite">
        {loading && <div className="msgr-search-status">Searching…</div>}
        {error && <div className="msgr-search-status msgr-search-error" role="alert">
            <span>{error}</span>
            <button type="button" onClick={() => setRetry(value => value + 1)}>Retry search</button>
        </div>}
        {!loading && !error && !any && <div className="msgr-empty">No results for “{term}”.</div>}
        {sections.filter(section => section.rows?.length).map(section => <section key={section.key} className="msgr-search-section">
            <h4><FontAwesomeIcon icon={section.icon} /> {section.label}</h4>
            {section.rows.map(section.render)}
        </section>)}
        {Object.values(results.more || {}).some(Boolean) && !loading && !error &&
            <button type="button" className="msgr-search-more" onClick={() => setPage(value => value + 1)}>Show more results</button>}
    </div>;
};

export default ChatSearchResults;
